import type { InstructionView, ThresholdMode } from '@/services/manualTradingApi';
import { normalizeSymbolInput } from '@/lib/exchangeInstId';

export function formatInstructionAmount(value: number): string {
  if (!Number.isFinite(value)) return '—';
  return value.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/** One-shot close tracks progress by token size, not notional. */
export function isSizeDrivenClose(row: InstructionView): boolean {
  return row.action === 'Close' && row.validity === 'one_shot' && row.target_size > 0;
}

export function isActiveInstruction(status: string): boolean {
  return status === 'Running' || status === 'Paused';
}

export function instructionPositionGroupKey(row: InstructionView): string {
  return `${normalizeSymbolInput(row.symbol)}|${row.long_exchange}|${row.short_exchange}`;
}

export function positionGroupInstructionKey(
  symbol: string,
  longExchange: string,
  shortExchange: string,
): string {
  return `${normalizeSymbolInput(symbol)}|${longExchange}|${shortExchange}`;
}

export function indexActiveInstructionsByGroupKey(
  instructions: InstructionView[],
): Map<string, InstructionView[]> {
  const map = new Map<string, InstructionView[]>();
  for (const row of instructions) {
    if (!isActiveInstruction(row.status)) continue;
    const key = instructionPositionGroupKey(row);
    const list = map.get(key);
    if (list) list.push(row);
    else map.set(key, [row]);
  }
  for (const list of map.values()) {
    list.sort((a, b) => {
      if (a.action !== b.action) return a.action === 'Open' ? -1 : 1;
      if (a.status !== b.status) return a.status === 'Running' ? -1 : 1;
      return a.instruction_id.localeCompare(b.instruction_id);
    });
  }
  return map;
}

export function formatInstructionThresholdMode(mode: ThresholdMode): string {
  switch (mode) {
    case 'Immediate':
      return '立即';
    case 'GreaterOrEqual':
      return '≥阈值';
    case 'LessOrEqual':
      return '≤阈值';
    default:
      return mode;
  }
}

export function formatInstructionThresholdValue(
  threshold: number | null,
  mode: ThresholdMode,
): string {
  if (mode === 'Immediate') return '立即';
  if (threshold == null || !Number.isFinite(threshold)) return '—';
  const pct = (threshold * 100).toFixed(4);
  if (mode === 'GreaterOrEqual') return `≥${pct}%`;
  if (mode === 'LessOrEqual') return `≤${pct}%`;
  return `${pct}%`;
}

/** Compact marker for chart price-line labels: ▶ running, || paused. */
export function instructionStatusChartMarker(status: string): string {
  return status === 'Paused' ? '||' : '▶';
}

export function formatInstructionStatusLabel(status: string): string {
  switch (status) {
    case 'Running':
      return '运行中';
    case 'Paused':
      return '已暂停';
    case 'Completed':
      return '已完成';
    case 'Cancelled':
      return '已取消';
    default:
      return status;
  }
}

export function formatInstructionValidityLabel(validity: InstructionView['validity']): string {
  return validity === 'persistent' ? '永久' : '单次';
}

export function formatActiveInstructionProgress(row: InstructionView): string {
  if (isSizeDrivenClose(row)) {
    return `${formatInstructionAmount(row.executed_size)}/${formatInstructionAmount(row.target_size)} token`;
  }
  if (row.action === 'Close' && row.validity === 'persistent') {
    return `单次 ${formatInstructionAmount(row.per_order_notional)} USD`;
  }
  return `${formatInstructionAmount(row.executed_notional)}/${formatInstructionAmount(row.target_notional)} USD`;
}

/** Compact one-line label for position group instruction chips. */
export function formatActiveInstructionChipText(row: InstructionView): string {
  const threshold = formatInstructionThresholdValue(row.threshold, row.threshold_mode);
  const progress = formatActiveInstructionProgress(row);
  const validity =
    row.action === 'Close' && row.validity === 'persistent' ? ' · 永久' : '';
  const status = formatInstructionStatusLabel(row.status);
  return `${threshold} · ${progress}${validity} · ${status}`;
}

export function formatActiveInstructionChipLabel(row: InstructionView): string {
  const action = row.action === 'Open' ? '开仓' : '关仓';
  return `${action} · ${formatActiveInstructionChipText(row)}`;
}

export function activeInstructionChipTitle(row: InstructionView): string {
  const lines = [
    `指令 ${row.instruction_id}`,
    `${row.action === 'Open' ? '开仓' : '关仓'} · ${formatInstructionValidityLabel(row.validity)} · ${formatInstructionThresholdMode(row.threshold_mode)}`,
    `阈值 ${formatInstructionThresholdValue(row.threshold, row.threshold_mode)}`,
    `进度 ${formatActiveInstructionProgress(row)}`,
    `状态 ${formatInstructionStatusLabel(row.status)}`,
    `Long ${row.long_exchange} · ${row.long_inst_id}`,
    `Short ${row.short_exchange} · ${row.short_inst_id}`,
  ];
  return lines.join('\n');
}

export function formatInstructionNotionalProgress(row: InstructionView): string {
  if (isSizeDrivenClose(row)) return '—';
  return `${formatInstructionAmount(row.target_notional)} / ${formatInstructionAmount(row.executed_notional)}`;
}

export function formatInstructionSizeProgress(row: InstructionView): string {
  if (!isSizeDrivenClose(row)) return '—';
  return `${formatInstructionAmount(row.target_size)} / ${formatInstructionAmount(row.executed_size)}`;
}

export function instructionNotionalProgressTitle(row: InstructionView): string | undefined {
  if (isSizeDrivenClose(row)) {
    return '单次关仓按 token size 累计；名义不参与完成判定';
  }
  return '目标 / 已执行 USD 名义';
}

export function instructionSizeProgressTitle(row: InstructionView): string | undefined {
  if (isSizeDrivenClose(row)) {
    return '目标 / 已执行 token 数量';
  }
  return '开仓与永久关仓不按 size 累计';
}

export function isInstructionProgressNa(value: string): boolean {
  return value === '—';
}
